import { ROUTES } from '@/constants/routes'
import { useMsalLogin } from '@/hooks/Auth/useMsalLogin'
import { PrivateLayout } from '@/layouts/PrivateLayout'
import { PublicLayout } from '@/layouts/PublicLayout'
import Loading from '@/shared/components/Loading'
import { useAuthStore } from '@/store/auth'
import { useSpacesStore } from '@/store/useSpacesStore'
import { useMsal } from '@azure/msal-react'
import { lazy, Suspense, useEffect } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

// Public pages
const LandingPage = lazy(() => import('@/pages/Landing'))

// Private pages
const Home = lazy(() => import('@/pages/Home'))
const HazardView = lazy(() => import('@/pages/HazardView'))
const Spaces = lazy(() => import('@/pages/Spaces'))
const Tools = lazy(() => import('@/pages/Tools'))
const Network = lazy(() => import('@/pages/Network'))
const ConversationPage = lazy(() => import('@/pages/ConversationPage'))
const ReportPage = lazy(() => import('@/pages/Report'))
const MarkdownTest = lazy(() => import('@/pages/MarkdownTest'))

/**
 * Protected route wrapper component
 * Handles auth loading and authenticated states
 */
const ProtectedRoute = () => {
  const location = useLocation()
  const { isAuthenticated, isLoading } = useMsalLogin()

  if (isLoading) {
    return <Loading />
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.ROOT} state={{ from: location }} replace />
  }

  return <PrivateLayout />
}

const AppRoutes = () => {
  const location = useLocation()
  const { instance, accounts } = useMsal()
  const { isAuthenticated, isLoading } = useMsalLogin()
  const { setIsAuthenticated } = useAuthStore()
  const { setSearchTerm } = useSpacesStore()

  // Check MSAL state and update auth store
  useEffect(() => {
    const currentAccount = instance.getActiveAccount() || accounts[0]
    if (currentAccount && !isAuthenticated && !isLoading) {
      // If we have an account but not authenticated in our store, update it
      setIsAuthenticated(true)
    }
  }, [instance, accounts, isAuthenticated, isLoading])

  useEffect(() => {
    const allowedSearchRoutes = [ROUTES.SPACES.CONVERSATIONS, ROUTES.SPACES.REPORTS]
    const isSearchRoute = allowedSearchRoutes.some((route) => location.pathname.startsWith(route))

    if (!isSearchRoute) {
      setSearchTerm('')
    }
  }, [location.pathname, setSearchTerm])

  // Show global loading state when authenticating
  if (isLoading) {
    return <Loading />
  }

  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        {/* Public Routes */}
        <Route element={<PublicLayout />}>
          <Route
            path={ROUTES.ROOT}
            element={
              isAuthenticated ? <Navigate to={ROUTES.DASHBOARD} state={{ from: location }} replace /> : <LandingPage />
            }
          />
          <Route path={ROUTES.MARKDOWN_TEST} element={<MarkdownTest />} />
        </Route>

        {/* Protected Routes */}
        <Route element={<ProtectedRoute />}>
          <Route path={`${ROUTES.CONVERSATION.ROOT}/:id`} element={<ConversationPage />} />
          <Route path={`${ROUTES.SPACES.REPORTS}/:id`} element={<ReportPage />} />
          <Route path={ROUTES.DASHBOARD} element={<Home />} />
          <Route path={`${ROUTES.HAZARD.ROOT}/:hazardType`} element={<HazardView />} />
          <Route path={`${ROUTES.SPACES.ROOT}/*`} element={<Spaces />} />
          <Route path={ROUTES.TOOLS.ROOT} element={<Tools />} />
          <Route path={ROUTES.NETWORK.ROOT} element={<Network />} />
          {/* Redirect all unknown routes to dashboard when authenticated */}
          <Route path="*" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
        </Route>
      </Routes>
    </Suspense>
  )
}

export default AppRoutes
